import React from 'react'
import axios from 'axios'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import { convertSchoolAbbreviationToFullName } from '../../Calendar/calendarFunctions'
import {
    canDeleteNoSmsRecordedRows,
    filterRecordedResponseRows,
} from './inquirySmsResponseUtils'
import './inquiry-sms-response.css'

const formatEasternDateTime = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'

    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(date)
}

const formatWorkingMinutes = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—'
    const minutes = Math.max(0, Math.round(Number(value)))
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainder = minutes % 60
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`
}

const formatSchool = (school) => {
    const value = String(school || '').trim()
    if (!value) return '—'
    return convertSchoolAbbreviationToFullName(value) || value.toUpperCase()
}

const MetricCard = ({ label, value, description, tone = 'neutral' }) => (
    <div className={`inquiry-sms-response__metric is-${tone}`}>
        <div className="inquiry-sms-response__metric-label">{label}</div>
        <strong>{value}</strong>
        <div className="inquiry-sms-response__metric-description">{description}</div>
    </div>
)

const StatusPill = ({ status }) => {
    const statusLabels = {
        on_time: 'On time',
        late: 'Late',
        pending: 'Pending',
        overdue_uncontacted: 'Overdue',
    }

    return (
        <span className={`inquiry-sms-response__status is-${status}`}>
            {statusLabels[status] || status}
        </span>
    )
}

const InquirySmsResponse = ({ userData }) => {
    const [summary, setSummary] = React.useState(null)
    const [loading, setLoading] = React.useState(true)
    const [refreshing, setRefreshing] = React.useState(false)
    const [error, setError] = React.useState('')
    const [deletingFamilyIds, setDeletingFamilyIds] = React.useState(() => new Set())
    const canDeleteNoSmsRows = canDeleteNoSmsRecordedRows(userData)

    const loadSummary = React.useCallback(async ({ silent = false } = {}) => {
        if (silent) {
            setRefreshing(true)
        } else {
            setLoading(true)
        }
        setError('')

        try {
            const response = await axios.get(
                `${baseUrl}/dashboard/inquiry-sms-response-summary`,
                getRequestHeaders()
            )
            setSummary(response?.data || null)
        } catch (requestError) {
            console.error('Unable to load inquiry SMS response metrics:', requestError)
            setError(
                requestError?.response?.data?.message ||
                requestError?.message ||
                'Unable to load inquiry SMS response metrics.'
            )
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [])

    React.useEffect(() => {
        loadSummary()
        const refreshTimer = window.setInterval(
            () => loadSummary({ silent: true }),
            60 * 1000
        )
        return () => window.clearInterval(refreshTimer)
    }, [loadSummary])

    const deleteNoSmsRecordedRow = async (row) => {
        const familyId = String(row?.familyId || '').trim()
        if (!canDeleteNoSmsRows || !familyId || deletingFamilyIds.has(familyId)) return

        const familyName = String(row?.familyName || 'this family').trim()
        const confirmed = window.confirm(
            `Remove ${familyName} from SMS response tracking? This will not delete the family or onboarding card.`
        )
        if (!confirmed) return

        setDeletingFamilyIds((current) => new Set(current).add(familyId))
        setError('')
        try {
            await axios.delete(
                `${baseUrl}/dashboard/inquiry-sms-response-summary/${encodeURIComponent(familyId)}`,
                getRequestHeaders()
            )
            await loadSummary({ silent: true })
        } catch (requestError) {
            console.error('Unable to remove inquiry from SMS response tracking:', requestError)
            setError(
                requestError?.response?.data?.message
                || requestError?.message
                || 'Unable to remove this inquiry from SMS response tracking.'
            )
        } finally {
            setDeletingFamilyIds((current) => {
                const next = new Set(current)
                next.delete(familyId)
                return next
            })
        }
    }

    if (loading) {
        return (
            <div className="inquiry-sms-response__message">
                Loading SMS response tracking…
            </div>
        )
    }

    if (error && !summary) {
        return (
            <div className="inquiry-sms-response__message is-error" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => loadSummary()}>Retry</button>
            </div>
        )
    }

    const data = summary || {}
    const rows = Array.isArray(data.rows) ? data.rows : []
    const uncontactedRows = Array.isArray(data.uncontactedRows)
        ? data.uncontactedRows
        : []
    const returnedContactedRows = Array.isArray(data.contactedRows)
        ? data.contactedRows
        : rows.filter((row) => row.responseAt)
    const contactedRows = filterRecordedResponseRows(returnedContactedRows)
    const medianResponse = data.medianResponseMinutes == null
        ? '—'
        : formatWorkingMinutes(data.medianResponseMinutes)

    return (
        <div className="inquiry-sms-response">
            <div className="inquiry-sms-response__toolbar">
                <div>
                    {/* <p>
                        A successful SMS Template clipboard copy is the recorded response. The
                        clock runs 10:00 AM–6:00 PM Eastern on working days and pauses on weekends
                        and observed US federal holidays.
                    </p> */}
                    <span>
                        Tracking coverage begins {formatEasternDateTime(data.trackingStartAt)}.
                        The target is {data.responseTargetMinutes || 15} working minutes.
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => loadSummary({ silent: true })}
                    disabled={refreshing}
                >
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            {error && (
                <div className="inquiry-sms-response__inline-error" role="alert">{error}</div>
            )}

            <div className="inquiry-sms-response__metrics">
                <MetricCard
                    label="On-time response rate"
                    value={`${Number(data.onTimeRate || 0).toFixed(1)}%`}
                    tone="good"
                    description={`${data.onTimeCount || 0} of ${data.dueInquiryCount || 0} inquiries currently due`}
                />
                <MetricCard
                    label="Median response"
                    value={medianResponse}
                    tone="neutral"
                    description="Working time to the latest text in the initial session"
                />
                <MetricCard
                    label="Overdue · no text"
                    value={Number(data.overdueUncontacted || 0).toLocaleString()}
                    tone={data.overdueUncontacted ? 'issue' : 'good'}
                    description="Past 15 working minutes with no text recorded"
                />
                <MetricCard
                    label="Repeat copies"
                    value={Number(data.repeatCopyAttempts || 0).toLocaleString()}
                    tone="neutral"
                    description={`${data.totalCopyAttempts || 0} total successful text attempts`}
                />
            </div>

            <div className="inquiry-sms-response__counts" aria-label="Inquiry response counts">
                <span><strong>{data.totalInquiries || 0}</strong> tracked inquiries</span>
                <span><strong>{data.contactedInquiries || 0}</strong> with a recorded text</span>
                <span><strong>{data.pendingUncontacted || 0}</strong> pending</span>
                <span><strong>{data.lateCount || 0}</strong> late responses</span>
                <span><strong>{data.followUpCopyAttempts || 0}</strong> follow-up texts</span>
            </div>

            <div className="inquiry-sms-response__grids">
                <section className="inquiry-sms-response__panel" aria-labelledby="uncontacted-inquiries-title">
                    <div className="inquiry-sms-response__panel-header">
                        <div>
                            <h4 id="uncontacted-inquiries-title">No sms recorded</h4>
                            <p>
                                Pending inquiries remain within the target; overdue inquiries need attention.
                            </p>
                        </div>
                        <strong>{data.noRecordedCopy || 0}</strong>
                    </div>
                    {uncontactedRows.length ? (
                        <div className="inquiry-sms-response__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Family</th>
                                        <th>School</th>
                                        <th>Students</th>
                                        <th>Received</th>
                                        <th>Working age</th>
                                        <th>Status</th>
                                        {canDeleteNoSmsRows && <th aria-label="Actions" />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {uncontactedRows.map((row) => (
                                        <tr key={row.familyId}>
                                            <td>{row.familyName || 'Unnamed family'}</td>
                                            <td>{formatSchool(row.school)}</td>
                                            <td>{row.studentCount || 0}</td>
                                            <td>{formatEasternDateTime(row.receivedAt)}</td>
                                            <td>{formatWorkingMinutes(row.currentBusinessAgeMinutes)}</td>
                                            <td><StatusPill status={row.status} /></td>
                                            {canDeleteNoSmsRows && (
                                                <td className="inquiry-sms-response__delete-cell">
                                                    <button
                                                        type="button"
                                                        className="inquiry-sms-response__delete"
                                                        aria-label={`Remove ${row.familyName || 'family'} from SMS response tracking`}
                                                        title="Remove from SMS response tracking"
                                                        disabled={deletingFamilyIds.has(String(row.familyId))}
                                                        onClick={() => deleteNoSmsRecordedRow(row)}
                                                    >
                                                        {deletingFamilyIds.has(String(row.familyId)) ? '…' : '×'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="inquiry-sms-response__empty">
                            Every tracked inquiry has a successful text sent.
                        </div>
                    )}
                    <div className="inquiry-sms-response__panel-note">
                        This report cannot detect texts sent manually without using the SMS Template button.
                    </div>
                </section>

                <section className="inquiry-sms-response__panel" aria-labelledby="recorded-responses-title">
                    <div className="inquiry-sms-response__panel-header">
                        <div>
                            <h4 id="recorded-responses-title">Recorded responses</h4>
                            {/* <p>
                                The latest successful copy within the four-working-hour initial session sets the response time.
                            </p> */}
                        </div>
                        <strong>{contactedRows.length}</strong>
                    </div>
                    {contactedRows.length ? (
                        <div className="inquiry-sms-response__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Family</th>
                                        <th>Admin</th>
                                        <th>Response</th>
                                        <th>Attempts</th>
                                        <th>Follow-ups</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contactedRows.map((row) => (
                                        <tr key={row.familyId}>
                                            <td>{row.familyName || 'Unnamed family'}</td>
                                            <td>{row.responseActorName || 'Unknown admin'}</td>
                                            <td>{formatWorkingMinutes(row.responseMinutes)}</td>
                                            <td>{row.attemptCount || 0}</td>
                                            <td>{row.followUpAttemptCount || 0}</td>
                                            <td><StatusPill status={row.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="inquiry-sms-response__empty">
                            No successful SMS-template copies have been recorded yet.
                        </div>
                    )}
                </section>
            </div>
        </div>
    )
}

export default InquirySmsResponse
